import { styled } from "@pigment-css/react";
import { ChevronRightThin } from "@acto/web-common/components";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { CalendarSelectMenu } from "./CalendarSelectMenu";

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBlock: theme.fieldSalesTwo.spacing[8],
  width: "100%",
}));

const Title = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[18],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  color: theme.fieldSalesTwo.colors.neutral[900],
  whiteSpace: "nowrap",
}));

const Controls = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
}));

const NavButton = styled("button")(({ theme }) => ({
  width: 48,
  height: 36,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.neutral[300]}`,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: "transparent",
  cursor: "pointer",
  color: theme.fieldSalesTwo.colors.neutral[900],
  WebkitTapHighlightColor: "transparent",
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  "& svg": { width: 16, height: 16 },
  ...pressInteraction(theme),
}));

// No ChevronLeftThin exists in web-common; mirror the right chevron for the back arrow.
const ChevronBack = styled(ChevronRightThin)({ transform: "rotate(180deg)" });

export interface CalendarMonthDropdown {
  /** Year shown above the month name, e.g. "2026". */
  year: string;
  /** Current month name, e.g. "Mai". */
  monthLabel: string;
  /** All selectable months for the visible year. */
  months: { index: number; label: string }[];
  /** Index (0–11) of the currently-visible month. */
  currentIndex: number;
  onSelect: (monthIndex: number) => void;
  /** aria-label for the dropdown trigger. */
  triggerLabel: string;
}

export interface CalendarViewDropdown {
  /** Week/month options shown in the menu. */
  options: { value: number; label: string }[];
  /** Index of the currently-active view. */
  currentIndex: number;
  onSelect: (viewIndex: number) => void;
  /** aria-label for the dropdown trigger. */
  triggerLabel: string;
}

interface CalendarHeaderProps {
  title: string;
  previousLabel: string;
  nextLabel: string;
  onPrevious: () => void;
  onNext: () => void;
  /** When provided, the left side renders a year + month-picker dropdown instead of the plain title. */
  monthDropdown?: CalendarMonthDropdown;
  /** When provided, the period title itself becomes the week/month view toggle (menu opens beneath it). */
  viewDropdown?: CalendarViewDropdown;
}

export function CalendarHeader({
  title,
  previousLabel,
  nextLabel,
  onPrevious,
  onNext,
  monthDropdown,
  viewDropdown,
}: CalendarHeaderProps) {
  return (
    <Header>
      {monthDropdown ? (
        <CalendarSelectMenu
          variant="text"
          caption={monthDropdown.year}
          options={monthDropdown.months.map((month) => ({ value: month.index, label: month.label }))}
          value={monthDropdown.currentIndex}
          onSelect={monthDropdown.onSelect}
          triggerLabel={monthDropdown.triggerLabel}
        />
      ) : viewDropdown ? (
        <CalendarSelectMenu
          variant="text"
          displayLabel={title}
          options={viewDropdown.options}
          value={viewDropdown.currentIndex}
          onSelect={viewDropdown.onSelect}
          triggerLabel={viewDropdown.triggerLabel}
          showItemIcons={false}
        />
      ) : (
        <Title>{title}</Title>
      )}
      <Controls>
        <NavButton type="button" aria-label={previousLabel} onClick={onPrevious}>
          <ChevronBack />
        </NavButton>
        <NavButton type="button" aria-label={nextLabel} onClick={onNext}>
          <ChevronRightThin />
        </NavButton>
      </Controls>
    </Header>
  );
}
